import {Component, OnInit, ViewChild} from '@angular/core';
import {Invoice} from '../../../shared/models/account/invoice';
import {Partner} from '../../../shared/models/res/partner';
import {Company} from '../../../shared/models/res/company';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {InvoiceLine} from '../../../shared/models/account/invoice-line';
import {MatDialog} from '@angular/material/dialog';
import {MatTable, MatTableDataSource} from '@angular/material/table';
import {Actions, ApiEndpoints} from '../../../shared/constants/ApiEndpoints';
import {RequestMethod, RestService} from '../../../shared/services/rest.service';
import {TokenService} from '../../../shared/services/token.service';
import {TaxService} from '../../../shared/services/tax.service';
import {ActivatedRoute, Router} from '@angular/router';
import {PageableResponse} from '../../../shared/models/datasource/pageable-response';
import {RounderPipe} from '../../../shared/utils/pipes';
import {Facility} from '../../../shared/models/accr/Facility';
import {GeneralConstant} from '../../../shared/constants/GeneralConstant';
import {PermissionsService} from '@ucx/ui-lib';
import {ResponseSnackbarService} from '../../../shared/services/response-snackbar.service';

@Component({
  selector: 'app-public-invoice-view',
  templateUrl: './public-invoice-view.component.html',
  styleUrls: ['./public-invoice-view.component.scss'],
  providers: [RounderPipe]
})
export class PublicInvoiceViewComponent implements OnInit {
  /**
   * Invoice data.
   */
  invoice: Invoice = new Invoice();

  /**
   * Invoice hash.
   */
  hash;
  /**
   * Partner.
   */
  partner;
  /**
   * Contact partner.
   */
  contactPartner: Partner;
  /**
   * Contact partner.
   */
  company: Company;
  /**
   * Form group.
   */
  formGroup: FormGroup;
  /**
   * Invoice lines.
   */
  items: InvoiceLine[];

  /**
   * Amount untaxed.
   */
  amountUntaxed = 0;
  amountBeforeDiscount = 0;

  facility: Facility;

  /**
   * Amount tax.
   */
  amountTax = 0;

  /**
   * Amount total.
   */
  amountTotal = 0;

  /**
   * Currency symbol.
   */
  currencySymbolBefore;
  currencySymbolAfter = ' USD';

  dataSource: any;

  @ViewChild(MatTable) table: MatTable<any>;
  displayedColumns: string[] = ['product', 'name', 'price', 'amountTotalBeforeDiscount', 'discount', 'amountTotal'];

  /**
   * Constructor.
   */
  constructor(private formBuilder: FormBuilder,
              private restService: RestService,
              private tokenService: TokenService,
              private dialog: MatDialog,
              private taxService: TaxService,
              private activatedRoute: ActivatedRoute,
              private permissionService: PermissionsService,
              private libPermissionService: PermissionsService,
              private responseSnackBar: ResponseSnackbarService,
              private router: Router) {
    const params = activatedRoute.snapshot.params;
    this.hash = params && params.hash ? decodeURIComponent(params.hash) : null;
  }

  ngOnInit() {
    if (this.hash) {
      this.getInvoiceData();
    }
  }

  /**
   * Get company data.
   */
  getCompanyData() {
    if (this.invoice) {
      this.restService.publicRequest<Company>(RequestMethod.GET, '/p1/' + ApiEndpoints.RES.COMPANY, `read/1`).subscribe(
        result => {
          if (result.success) {
            this.company = result.data;
          }
        }
      );
    }
  }

  onUpdateInvoice(invoice: Invoice) {
  }

  /**
   * create payment modal
   */
  createPaymentModal() {
    this.router.navigateByUrl(`payment/${this.facility.id}/checkout?location=invoice`);
  }

  /**
   * Calculate amount.
   */
  computePrices() {
    let unTaxedAmount = 0;
    let taxAmount = 0;
    let totalAmount = 0;
    this.items.forEach(value => {
      const basePrice = value.price * value.quantity;
      let priceUnTaxed = basePrice;
      if (value.discount > 0 && value.discount <= 100) {
        priceUnTaxed = basePrice - ((value.discount / 100) * basePrice);
      }

      const priceTax = value.tax ? this.taxService.computeTaxes(value.tax, priceUnTaxed, value.quantity) : 0;
      unTaxedAmount += priceUnTaxed;
      taxAmount += priceTax;
      totalAmount += priceUnTaxed + priceTax;
    });
    this.amountUntaxed = unTaxedAmount;
    this.amountTax = taxAmount;
    this.amountTotal = totalAmount;
  }

  /**
   * Checkout.
   * @param event
   */
  onCheckout(event) {

  }

  /**
   * Form initialization.
   */
  private initForm() {
    this.formGroup = this.formBuilder.group({
      number: new FormControl(this.invoice.number || ''),
      date: new FormControl({
        value: (this.invoice.date ? new Date(this.invoice.date) : null),
        disabled: true
      }, Validators.required),
      dateDue: new FormControl({
        value: (this.invoice.dateDue ? new Date(this.invoice.dateDue) : null),
        disabled: true
      }, Validators.required),
      partner: new FormControl({
        value: (this.invoice.partner ? this.invoice.partner.id : null),
        disabled: true
      }, Validators.required),
      items: this.formBuilder.array([])
    });

    this.amountBeforeDiscount = this.invoice?.amountTotalBeforeDiscount || this.invoice.amountTotal || 0;
    this.amountUntaxed = this.invoice.amountUntaxed || 0;
    this.amountTax = this.invoice.amountTax || 0;
    this.amountTotal = this.invoice.amountTotal || 0;

    if (this.invoice.lines && this.invoice.lines.length > 0) {
      this.dataSource = new MatTableDataSource(this.invoice.lines);
    }
    if (this.invoice.partner) {
      this.partner = this.invoice.partner;
    }
  }

  /**
   * Get invoice data.
   */
  private getInvoiceData() {
    if (this.hash) {
      const data = {
        hash: this.hash
      };
      this.restService.publicRequest<any>(RequestMethod.POST, '/p1/' + ApiEndpoints.ACCOUNT.INVOICE, `show_invoice`, {body: data}).subscribe(
        result => {
          if (result.success && result.data) {
            this.invoice = result.data;
            this.partner = this.invoice.partner;
            this.getFacility();
            this.getCompanyData();
            if (this.invoice.currency) {
              const currency = this.invoice.currency;
              if (currency.position === 'before') {
                this.currencySymbolBefore = currency.symbol;
              } else {
                this.currencySymbolAfter = currency.symbol;
              }
            }
            this.getChildByParent();
            this.initForm();
          }
        }
      );
    }
  }

  private getFacility() {
    this.restService.publicRequest<any>(RequestMethod.GET, '/p1/' + ApiEndpoints.RES.FACILITY, `read/billing/guest/${this.invoice.partner.id}`).subscribe(
      response => {
        if (response.success && response.data) {
          this.facility = response.data;
        }
      }
    );
  }

  /**
   * Get contact info of partner.
   */
  private getChildByParent() {
    const data = {
      hash: this.hash
    };
    this.restService.publicRequest<PageableResponse<any>>(RequestMethod.POST, '/p1/' + ApiEndpoints.RES.PARTNER, Actions.BASE_ACTIONS.READ + '/contact', {body: data}).subscribe(
      response => {
        if (response.success && response.data && response.data.items.length > 0) {
          const res = response.data.items[0];
          res.zipCode = res.zipcode;
          res.state = res.state ? JSON.parse(res.state) : null;
          res.country = res.country ? JSON.parse(res.country) : null;
          this.contactPartner = res;
        }
      });
  }
}
